import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Chip,
  Grid,
  Stack,
  Box,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ButtonGroup,
  Tooltip,
  Collapse,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close,
  QrCodeScanner,
  Add,
  Remove,
  Delete,
  Archive,
  Assignment,
  Pause,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  Keyboard
} from '@mui/icons-material';
import AutoFocusScanner, { AutoFocusScannerRef } from './ScannerComponent';
import BarcodeDisplay from './BarcodeDisplay';
import Loader from '../../../components/Loader';
import { BoxProp, BoxStatus, ShipmentStatus } from '../../../types/Shipment.types';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';
import { useAutoSaveBoxItems } from '../../../hooks/useAutoSaveBoxItems';
import { useBoxItemsBackup } from './BoxItemsBackup';

interface BoxDialogProps {
  open: boolean;
  onClose: () => void;
  box: BoxProp | null;
  shipmentStatus: ShipmentStatus;
  onAddItem: (boxId: string, barcode: string) => void;
  onUpdateItemQuantity: (boxId: string, itemId: string, change: number) => void;
  onUpdateBoxStatus: (boxId: string, status: BoxStatus) => Promise<void>;
  onPrintBoxLabel: (box: BoxProp) => void;
  onPrintItemsList: (box: BoxProp) => void;
  processing?: boolean;
}

// Removed - no longer needed

const BoxDialog: React.FC<BoxDialogProps> = ({
  open,
  onClose,
  box,
  shipmentStatus,
  onAddItem,
  onUpdateItemQuantity,
  onUpdateBoxStatus,
  onPrintBoxLabel,
  onPrintItemsList,
  processing = false
}) => {
  // ------------ Hooks (ALWAYS at top level, fixed order) ------------
  const scannerRef = useRef<AutoFocusScannerRef>(null);
  const [itemScannerInput, setItemScannerInput] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchingBarcode] = useState(false); // search disabled here
  const [searchTerm, setSearchTerm] = useState('');
  const dispatch = useAppDispatch();

  // Removed - using simple backup system instead

  const canModifyBox =
    !!box && (box.status === BoxStatus.New || box.status === BoxStatus.Paused || box.status === BoxStatus.Completed);
  const isReviewMode = box?.status === BoxStatus.Completed;

  const { saveBoxItems, isAutoSaving, hasPendingChanges } = useAutoSaveBoxItems({
    box,
    enabled: open && canModifyBox,
    debounceMs: 10000,
    maxInterval: 120000,
    retryAttempts: 3
  });

  // Simple backup system for scanned items
  const { clearBackup } = useBoxItemsBackup({ box, open });

  // Reset inputs when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setItemScannerInput('');
      setSearchTerm('');
      setExpandedItems(new Set());
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open || !box) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd + P: Print box label
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        onPrintBoxLabel(box);
      }

      // Ctrl/Cmd + I: Print items list
      if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
        e.preventDefault();
        if (box.boxItems.length > 0) {
          onPrintItemsList(box);
        }
      }

      // Esc: Close dialog (built-in MUI, but we document it)
      // Already handled by MUI Dialog
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, box, onPrintBoxLabel, onPrintItemsList]);

  // Removed - no longer needed

  // Removed - no longer persisting expanded items or inputs

  // Simple activity tracking (removed persistent state)
  const bumpLastActivity = useCallback(() => {
    // Activity tracking removed - backup system handles item preservation
  }, []);

  // Close handler - clear backup when properly closing
  const handleClose = useCallback(() => {
    // Clear backup since user is properly closing
    clearBackup();
    onClose();

    // Return focus to the trigger element if available
    // This improves keyboard navigation flow
    setTimeout(() => {
      const triggerButton = document.querySelector('[data-box-trigger="true"]') as HTMLElement;
      triggerButton?.focus();
    }, 100);
  }, [onClose, clearBackup]);

  // Toggle expand
  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
    bumpLastActivity();
  }, [bumpLastActivity]);

  // Auto-focus scanner when dialog opens - improved timing
  useEffect(() => {
    if (open && box && shipmentStatus !== ShipmentStatus.Completed) {
      // Use requestAnimationFrame for better timing after render
      const rafId = requestAnimationFrame(() => {
        const timer = setTimeout(() => {
          scannerRef.current?.focus();
        }, 400); // Increased delay for reliability on slower devices
        return () => clearTimeout(timer);
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [open, box, shipmentStatus]);

  // Status chip config
  const getBoxStatusConfig = (status: BoxStatus) => {
    switch (status) {
      case BoxStatus.New:
        return { color: 'info' as const, label: 'New', bgcolor: '#e3f2fd', textColor: '#1976d2' };
      case BoxStatus.Paused:
        return { color: 'warning' as const, label: 'Paused', bgcolor: '#fff3e0', textColor: '#f57c00' };
      case BoxStatus.Completed:
        return { color: 'success' as const, label: 'Completed', bgcolor: '#e8f5e8', textColor: '#2e7d32' };
      default:
        return { color: 'default' as const, label: String(status), bgcolor: '#f5f5f5', textColor: '#757575' };
    }
  };

  // Simple input change handler
  const handleInputChange = useCallback((v: string) => {
    if (canModifyBox) {
      setItemScannerInput(v);
    } else {
      setSearchTerm(v);
    }
  }, [canModifyBox]);

  // Removed - backup system handles saving now

  // Removed periodic autosave - backup system handles item preservation

  // Removed - backup system handles visibility changes

  // Removed - backup system handles beforeunload

  // Simple scan handler
  const handleScan = useCallback((barcode: string) => {
    if (!box || !barcode.trim()) return;

    if (canModifyBox && !isUpdatingStatus) {
      onAddItem(box.id, barcode.trim());
      // Backup system will automatically save the items after they're added
    } else {
      setSearchTerm(barcode.trim());
    }
  }, [box, canModifyBox, isUpdatingStatus, onAddItem]);

  // Status update
  const handleStatusUpdate = async (newStatus: BoxStatus) => {
    if (!box || isUpdatingStatus) return;

    setIsUpdatingStatus(true);
    try {
      await onUpdateBoxStatus(box.id, newStatus);
      handleClose();
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // --------- Guarded values (safe when box is null) ----------
  const statusConfig = getBoxStatusConfig(box?.status ?? BoxStatus.New);
  const uniqueItemsCount = box?.boxItems.length ?? 0;
  const totalQty = useMemo(() => (box?.boxItems ?? []).reduce((sum, item) => sum + item.qty, 0), [box?.boxItems]);
  const scanRecords = totalQty; // as requested: use qty

  // ------------ Render (safe early return AFTER hooks) ------------
  if (!box) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="box-dialog-title"
      aria-describedby="box-dialog-description"
      PaperProps={{
        sx: {
          height: '90vh',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        },
        role: 'dialog',
        'aria-modal': true
      }}
    >
      {/* Loader Overlay */}
      {isUpdatingStatus && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Loader
            variant="overlay"
            type="warehouse"
            size="medium"
            message="Updating Box Status..."
            subMessage="Please wait while we save your changes"
          />
        </Box>
      )}

      <DialogTitle id="box-dialog-title">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h6" component="h2">{box.refNumber}</Typography>
              <Chip
                label={statusConfig.label}
                size="small"
                sx={{ bgcolor: statusConfig.bgcolor, color: statusConfig.textColor, fontWeight: 'bold' }}
              />
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
              <Typography variant="caption" color="text.secondary">
                Barcode:
              </Typography>
              <BarcodeDisplay value={box.barcode} width={120} height={25} showText style="compact" />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={
                <Box sx={{ p: 1.5, bgcolor: 'grey.900' }}>
                  <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ color: 'white' }}>
                    Keyboard Shortcuts
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'grey.100', mb: 0.5 }}>
                    • Ctrl+P: Print Label
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'grey.100', mb: 0.5 }}>
                    • Ctrl+I: Print Items
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'grey.100', mb: 0.5 }}>
                    • Esc: Close Dialog
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ color: 'grey.100' }}>
                    • Enter: Scan Item
                  </Typography>
                </Box>
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'grey.900',
                    '& .MuiTooltip-arrow': {
                      color: 'grey.900',
                    },
                  },
                },
              }}
            >
              <IconButton size="small" disabled={isUpdatingStatus}>
                <Keyboard />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={handleClose}
              disabled={isUpdatingStatus}
              aria-label="Close dialog"
            >
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ overflow: 'auto', flex: 1 }}>
        <Stack spacing={3}>
          {/* Box Stats */}
          <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={3} textAlign="center">
              <Grid item xs={4}>
                <Typography variant="h5" color="primary">
                  {uniqueItemsCount}
                </Typography>
                <Typography variant="caption">Unique Items</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h5" color="success.main">
                  {totalQty}
                </Typography>
                <Typography variant="caption">Total Quantity</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="h5" color="info.main">
                  {scanRecords}
                </Typography>
                <Typography variant="caption">Scan Records</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Item Scanner - Hide when shipment is completed */}
          {shipmentStatus !== ShipmentStatus.Completed && (
            <Paper sx={{ p: 2, border: 2, borderColor: 'primary.200', bgcolor: 'primary.50' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  color={canModifyBox ? 'primary' : 'info'}
                >
                  {isReviewMode
                    ? '🔍 Review Mode - Add/Edit Items (Press Enter to scan)'
                    : canModifyBox
                    ? '🔍 Add Item to This Box (Press Enter to scan)'
                    : '🔍 Search Items in This Box'}
                </Typography>
              </Stack>
              {isReviewMode && (
                <Typography variant="caption" color="warning.main" sx={{ mb: 1, display: 'block' }}>
                  ⚠️ Box is completed but you can still review and modify items
                </Typography>
              )}
              <AutoFocusScanner
                ref={scannerRef}
                fullWidth
                size="small"
                placeholder={
                  isReviewMode
                    ? 'Review - scan or enter item barcode...'
                    : canModifyBox
                    ? 'Scan or enter item barcode...'
                    : 'Search items by barcode...'
                }
                value={canModifyBox ? itemScannerInput : searchTerm}
                onChange={handleInputChange}
                onScan={handleScan}
                autoFocus
                autoSelect
                clearAfterScan={canModifyBox}
                focusDelay={200}
                disabled={isUpdatingStatus || searchingBarcode}
                aria-label={
                  isReviewMode
                    ? 'Scan or enter item barcode for review'
                    : canModifyBox
                    ? 'Scan or enter item barcode to add to box'
                    : 'Search items by barcode'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderWidth: 2, borderColor: 'primary.main' }
                  }
                }}
              />
            </Paper>
          )}

          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Scanned Items ({box.boxItems.length})
              </Typography>
              {canModifyBox && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!isAutoSaving && hasPendingChanges && (
                    <Typography variant="caption" color="warning.main">
                      Auto-save in 10s
                    </Typography>
                  )}
                  {!isAutoSaving && !hasPendingChanges && box.boxItems.length > 0 && (
                    <Typography variant="caption" color="success.main">
                      ✓ Saved
                    </Typography>
                  )}
                </Stack>
              )}
            </Stack>

            {box.boxItems.length > 0 ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ maxHeight: '50vh', overflow: 'auto' }}
                role="region"
                aria-label="Scanned items list"
              >
                <Table size="small" stickyHeader aria-label="Box items table">
                  <TableHead>
                    <TableRow sx={{ '& .MuiTableCell-head': { bgcolor: 'grey.50' } }}>
                      <TableCell>
                        <strong>#</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Item Details</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Properties</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Quantity</strong>
                      </TableCell>
                      {(shipmentStatus === ShipmentStatus.InReception ||
                        shipmentStatus === ShipmentStatus.Received ||
                        shipmentStatus === ShipmentStatus.Analyzing ||
                        shipmentStatus === ShipmentStatus.Analyzed ||
                        shipmentStatus === ShipmentStatus.Completing) && (
                        <TableCell align="center">
                          <strong>Actions</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {box.boxItems.map((item, index) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Chip label={index + 1} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="600">
                              {item.unitDetails?.itemName || `Item ${item.barcode}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.barcode}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {item.unitDetails?.itemAdditionalProperties ? (
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                  {Object.entries(item.unitDetails.itemAdditionalProperties)
                                    .slice(0, 2)
                                    .map(([key, value]) => {
                                      if (value === '' || value == null) return null;
                                      return (
                                        <Chip
                                          key={key}
                                          label={`${key}: ${value as any}`}
                                          size="small"
                                          variant="outlined"
                                          color={
                                            key === 'Brand'
                                              ? 'primary'
                                              : key === 'Color'
                                              ? 'secondary'
                                              : key === 'size'
                                              ? 'success'
                                              : 'default'
                                          }
                                          sx={{ fontSize: '0.65rem', minHeight: 'auto' }}
                                        />
                                      );
                                    })}
                                </Stack>
                                {Object.keys(item.unitDetails.itemAdditionalProperties).filter(k => {
                                  const v = item.unitDetails!.itemAdditionalProperties![k];
                                  return v !== '' && v != null;
                                }).length > 2 && (
                                  <Tooltip title={expandedItems.has(item.id) ? 'Show less' : 'Show more properties'}>
                                    <IconButton size="small" onClick={() => toggleExpanded(item.id)}>
                                      {expandedItems.has(item.id) ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                              <Collapse in={expandedItems.has(item.id)}>
                                <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap">
                                  {Object.entries(item.unitDetails.itemAdditionalProperties)
                                    .slice(2)
                                    .map(([key, value]) => {
                                      if (value === '' || value == null) return null;
                                      return (
                                        <Chip
                                          key={key}
                                          label={`${key}: ${value as any}`}
                                          size="small"
                                          variant="outlined"
                                          color={
                                            key === 'Brand'
                                              ? 'primary'
                                              : key === 'Color'
                                              ? 'secondary'
                                              : key === 'size'
                                              ? 'success'
                                              : 'default'
                                          }
                                          sx={{ fontSize: '0.65rem', minHeight: 'auto', mb: 0.5 }}
                                        />
                                      );
                                    })}
                                </Stack>
                              </Collapse>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No additional properties
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                            {(shipmentStatus === ShipmentStatus.Received ||
                              shipmentStatus === ShipmentStatus.Analyzing ||
                              shipmentStatus === ShipmentStatus.Analyzed ||
                              shipmentStatus === ShipmentStatus.Completing) ? (
                              <ButtonGroup size="small" variant="outlined">
                                <IconButton
                                  size="small"
                                  onClick={() => onUpdateItemQuantity(box.id, item.id, -1)}
                                  disabled={item.qty <= 1 || isUpdatingStatus}
                                  color="error"
                                >
                                  <Remove />
                                </IconButton>
                                <Button disabled sx={{ minWidth: 40 }}>
                                  {item.qty}
                                </Button>
                                <IconButton
                                  size="small"
                                  onClick={() => onUpdateItemQuantity(box.id, item.id, 1)}
                                  disabled={isUpdatingStatus}
                                  color="success"
                                >
                                  <Add />
                                </IconButton>
                              </ButtonGroup>
                            ) : (
                              <Stack alignItems="center">
                                <Chip label={item.qty} size="small" color="success" />
                                {item.unitDetails && (
                                  <Typography variant="caption" color="text.secondary">
                                    {item.unitDetails.unitOfMeasurement}
                                  </Typography>
                                )}
                              </Stack>
                            )}
                          </Stack>
                        </TableCell>
                        {(shipmentStatus === ShipmentStatus.Received ||
                          shipmentStatus === ShipmentStatus.Analyzing ||
                          shipmentStatus === ShipmentStatus.Analyzed ||
                          shipmentStatus === ShipmentStatus.Completing) && (
                          <TableCell align="center">
                            <Tooltip title="Remove item completely">
                              <IconButton
                                size="small"
                                onClick={() => onUpdateItemQuantity(box.id, item.id, -item.qty)}
                                disabled={isUpdatingStatus}
                                color="error"
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <QrCodeScanner sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                <Typography color="text.secondary">No items scanned yet</Typography>
                <Typography variant="caption" color="text.secondary">
                  Use the scanner above to add items
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={1} flex={1}>
          <Button
            variant="outlined"
            color="info"
            startIcon={<Archive />}
            onClick={() => onPrintBoxLabel(box)}
            aria-label={`Print label for box ${box.refNumber}`}
          >
            Print Label
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<Assignment />}
            onClick={() => onPrintItemsList(box)}
            disabled={box.boxItems.length === 0}
            aria-label={`Print items list for box ${box.refNumber}`}
          >
            Print Items
          </Button>

          {(shipmentStatus === ShipmentStatus.InReception ||
            shipmentStatus === ShipmentStatus.Received ||
            shipmentStatus === ShipmentStatus.Analyzing ||
            shipmentStatus === ShipmentStatus.Analyzed ||
            shipmentStatus === ShipmentStatus.Completing) && (
            <>
              {box.status !== BoxStatus.Completed && (
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<Pause />}
                  onClick={() => handleStatusUpdate(BoxStatus.Paused)}
                  disabled={isUpdatingStatus}
                >
                  Pause
                </Button>
              )}

              <Button
                variant="contained"
                color={box.status === BoxStatus.Completed ? 'primary' : 'success'}
                startIcon={<CheckCircle />}
                onClick={() => handleStatusUpdate(BoxStatus.Completed)}
                disabled={box.boxItems.length === 0 || isUpdatingStatus}
              >
                {box.status === BoxStatus.Completed ? 'Update Box' : 'Complete Box'}
              </Button>
            </>
          )}
        </Stack>
        <Button variant="outlined" onClick={handleClose} disabled={isUpdatingStatus}>
          Close
        </Button>
      </DialogActions>

      {/* Auto-saving Snackbar */}
      <Snackbar
        open={isAutoSaving}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: 80 }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{
            minWidth: 200,
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
        >
          Auto-saving...
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default BoxDialog;
